import { baseUrl, getTokenFromLocalStorage } from "./Configuration";

export interface RenewalApiOccupant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  isCosigner: boolean;
}

export interface RenewalApiEntry {
  id: string;
  publicApartmentUnitId: string;
  hasRenewalRecord: boolean;
  unit: string;
  apartmentTitle: string;
  building?: string;
  buildingAddress?: string;
  tenant: string;
  email: string;
  phone: string;
  rent: number;
  newRent: number;
  proposedRent?: number;
  deposit: number;
  leaseFrom?: string;
  leaseTo?: string;
  lateCount: number;
  sourceStatus: string;
  sourceMappedStatus: string;
  sourceStage: string;
  dashboardStatus: string;
  dashboardStage: string;
  hasStatusMismatch: boolean;
  notes: string;
  actualLine?: string;
  renewalLine?: string;
  comparableGroup?: string;
  occupants: RenewalApiOccupant[];
}

export interface RenewalAttentionApiEntry {
  attentionType: "no_lease_date" | "expired" | "not_renewing" | "being_marketed";
  entry: RenewalApiEntry;
}

export interface RenewalPatchPayload {
  dashboardStatus?: string;
  newRent?: number;
  currentRent?: number;
  notes?: string;
  renewalLine?: string;
  comparableGroup?: string;
}

async function authedFetch(url: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
      ...(options.headers || {}),
    },
  });

  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP error! Status: ${response.status}`);
  }

  return response;
}

export async function fetchRenewals(): Promise<RenewalApiEntry[]> {
  const response = await authedFetch(`${baseUrl}/api/renewals`);
  return (await response.json()) as RenewalApiEntry[];
}

export async function fetchAttentionUnits(): Promise<RenewalAttentionApiEntry[]> {
  const response = await authedFetch(`${baseUrl}/api/renewals/attention`);
  return (await response.json()) as RenewalAttentionApiEntry[];
}

export async function fetchRenewal(id: string): Promise<RenewalApiEntry> {
  const response = await authedFetch(`${baseUrl}/api/renewals/${id}`);
  return (await response.json()) as RenewalApiEntry;
}

export async function updateRenewal(
  id: string,
  payload: RenewalPatchPayload,
): Promise<RenewalApiEntry> {
  const response = await authedFetch(`${baseUrl}/api/renewals/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return (await response.json()) as RenewalApiEntry;
}

export async function sendRenewalEmail(
  id: string,
  payload: { recipients: string[]; subject: string; body: string },
): Promise<void> {
  await authedFetch(`${baseUrl}/api/renewals/${id}/send-email`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface RenewalMessage {
  id: string;
  threadId: string;
  from: string;
  to: string;
  subject: string;
  date: string | null;
  snippet: string;
  body: string;
  messageId: string;
}

export interface RenewalThread {
  threadId: string;
  subject: string;
  snippet: string;
  lastMessageDate: string | null;
  messages: RenewalMessage[];
}

export async function fetchRenewalConversation(
  id: string,
): Promise<RenewalThread[]> {
  const response = await authedFetch(`${baseUrl}/api/renewals/${id}/conversation`);
  return (await response.json()) as RenewalThread[];
}

export async function sendRenewalReply(
  id: string,
  payload: {
    toEmail: string;
    subject: string;
    body: string;
    threadId?: string;
    replyToMessageId?: string;
  },
): Promise<void> {
  await authedFetch(`${baseUrl}/api/renewals/${id}/reply`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
